import { Fragment, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  BellIcon,
  HomeIcon,
  BuildingOfficeIcon,
  DocumentDuplicateIcon,
  HeartIcon,
  BeakerIcon,
  ChartBarIcon,
  DocumentTextIcon,
  PhotoIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  UserGroupIcon,
  UserCircleIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  ReceiptPercentIcon,
  CreditCardIcon,
  CalculatorIcon,
  PresentationChartLineIcon,
  TruckIcon,
  ScaleIcon,
  CogIcon,
  SparklesIcon,
  BookOpenIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  CakeIcon,
  LockClosedIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import type { FeatureKey } from '../api/billing';
import ParrotEmblem from './ui/ParrotEmblem';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import { getInstanceInfo, shortInstanceId } from '../config/instance';
import { getLegalLinks } from '../config/legal';
import { getSupportLinks } from '../config/support';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  feature?: FeatureKey;
  adminOnly?: boolean;
}
interface NavSection {
  name: string;
  items: NavItem[];
}

// Main navigation sections
const navigation: NavSection[] = [
  {
    name: 'Overview',
    items: [
      { name: 'Dashboard', href: '/', icon: HomeIcon },
      { name: 'Financial Dashboard', href: '/financial-dashboard', icon: PresentationChartLineIcon },
      { name: 'Reports', href: '/reports', icon: ChartBarIcon },
      { name: 'Notifications', href: '/notifications', icon: BellIcon },
      { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentListIcon },
      { name: 'BirdChatter', href: '/birdchatter', icon: SparklesIcon, feature: 'birdchatter' },
      { name: 'Knowledge Library', href: '/knowledge', icon: BookOpenIcon, feature: 'knowledge_library' },
    ]
  },
  {
    name: 'Marketplace',
    items: [
      { name: 'Marketplace Dashboard', href: '/marketplace', icon: GlobeAltIcon, feature: 'marketplace' },
    ]
  },
  {
    name: 'Bird Management',
    items: [
      { name: 'Species', href: '/species', icon: BuildingOfficeIcon },
      { name: 'Birds', href: '/birds', icon: DocumentDuplicateIcon },
      { name: 'Breeding Pairs', href: '/breeding', icon: HeartIcon },
      { name: 'Clutches', href: '/clutches', icon: BeakerIcon },
      { name: 'Chicks', href: '/chicks', icon: CakeIcon },
      { name: 'Cages', href: '/cages', icon: RectangleGroupIcon },
      { name: 'Health Management', href: '/health-management', icon: ShieldCheckIcon },
      { name: 'Nutrition Management', href: '/nutrition-management', icon: CubeIcon },
    ]
  },
  {
    name: 'Financial Management',
    items: [
      { name: 'Sales', href: '/sales', icon: BanknotesIcon },
      { name: 'Purchases', href: '/purchases', icon: ShoppingCartIcon },
      { name: 'Expenses', href: '/expenses', icon: ReceiptPercentIcon },
      { name: 'Payments', href: '/payments', icon: CreditCardIcon },
      { name: 'Transactions', href: '/transactions', icon: CalculatorIcon },
      { name: 'Accounting', href: '/accounting', icon: CalculatorIcon },
    ]
  },
  {
    name: 'Inventory & Operations',
    items: [
      { name: 'Supplies', href: '/supplies', icon: TruckIcon },
      { name: 'Suppliers', href: '/suppliers', icon: BuildingOffice2Icon },
      { name: 'Customers', href: '/customers', icon: UserGroupIcon },
      { name: 'Equipment', href: '/equipment', icon: ScaleIcon },
    ]
  },
  {
    name: 'Documentation',
    items: [
      { name: 'Documents', href: '/documents', icon: DocumentTextIcon },
      { name: 'Photos', href: '/photos', icon: PhotoIcon },
      { name: 'Notes', href: '/notes', icon: ClipboardDocumentListIcon },
    ]
  },
  {
    name: 'Administration',
    items: [
      { name: 'My BirdMan', href: '/portal', icon: HomeIcon },
      { name: 'My profile', href: '/profile', icon: UserCircleIcon },
      { name: 'Users', href: '/users', icon: UserCircleIcon, adminOnly: true },
      { name: 'Operations', href: '/admin', icon: ShieldCheckIcon, adminOnly: true },
      { name: 'System Settings', href: '/system-settings', icon: CogIcon },
    ]
  },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const { status: entitlements, hasFeature } = useEntitlements();
  const reducedMotion = usePrefersReducedMotion();
  const instance = getInstanceInfo();
  const instanceLabel = instance.name || (instance.id ? `Instance ${shortInstanceId(instance.id)}` : null);
  const legalLinks = getLegalLinks();
  const supportHelpUrl = getSupportLinks().help;
  const showPlan = !!entitlements?.billingConfigured;

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="relative">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="modal-overlay" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-slate-200/60 bg-white/95 px-6 pb-4 backdrop-blur-md">
                  <div className="flex h-16 shrink-0 items-center">
                    <ParrotEmblem className={`h-9 w-9 ${reducedMotion ? '' : 'animate-[float_5s_ease-in-out_infinite]'}`} animated={!reducedMotion} />
                    <span className="ml-2 text-xl font-semibold gradient-title">BirdMan</span>
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      {navigation.map((section) => (
                        <li key={section.name}>
                          <div className="text-xs font-semibold leading-6 text-slate-600 uppercase tracking-wider">
                            {section.name}
                          </div>
                          <ul role="list" className="-mx-2 mt-2 space-y-1">
                            {section.items.filter((item) => !item.adminOnly || isAdmin).map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  className={`
                                    group flex gap-x-3 rounded-xl p-2 text-sm leading-6 font-semibold transition-all duration-200
                                    ${location.pathname === item.href
                                      ? 'glass-panel text-sky-700 shadow-sm'
                                      : 'text-slate-600 hover:text-sky-700 hover:bg-white/70'
                                    }
                                  `}
                                >
                                  <item.icon
                                    className={`h-6 w-6 shrink-0 ${
                                      location.pathname === item.href ? 'text-sky-600' : 'text-slate-400 group-hover:text-sky-600'
                                    }`}
                                    aria-hidden="true"
                                  />
                                  <span className="flex-1">{item.name}</span>
                                  {item.feature && !hasFeature(item.feature) && (
                                    <LockClosedIcon className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                                  )}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                      <li className="mt-auto">
                        <div className="soft-separator border-t pt-4">
                          <div className="flex items-center gap-x-3 px-2 py-2">
                            <UserCircleIcon className="h-8 w-8 text-sky-500" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">
                                {user?.firstName} {user?.lastName}
                              </p>
                              <p className="text-xs text-slate-500">{user?.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={handleLogout}
                            className="group flex w-full gap-x-3 rounded-xl p-2 text-sm leading-6 font-semibold text-slate-600 transition-all duration-200 hover:bg-rose-50 hover:text-rose-600"
                          >
                            <ArrowLeftOnRectangleIcon className="h-6 w-6 shrink-0 text-slate-400 group-hover:text-rose-600" />
                            Logout
                          </button>
                        </div>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="glass-panel m-3 flex grow flex-col gap-y-5 overflow-y-auto rounded-3xl border border-slate-200/60 bg-gradient-to-b from-white/90 via-sky-50/70 to-violet-100/50 px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center">
            <ParrotEmblem className={`h-9 w-9 ${reducedMotion ? '' : 'animate-[float_5s_ease-in-out_infinite]'}`} animated={!reducedMotion} />
            <span className="ml-2 text-xl font-semibold gradient-title">BirdMan</span>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              {navigation.map((section) => (
                <li key={section.name}>
                  <div className="text-xs font-semibold leading-6 text-slate-600 uppercase tracking-wider">
                    {section.name}
                  </div>
                  <ul role="list" className="-mx-2 mt-2 space-y-1">
                    {section.items.filter((item) => !item.adminOnly || isAdmin).map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`
                            group flex gap-x-3 rounded-xl p-2 text-sm leading-6 font-semibold transition-all duration-200
                            ${location.pathname === item.href
                              ? 'glass-panel text-sky-700 shadow-sm'
                              : 'text-slate-600 hover:bg-white/70 hover:text-sky-700'
                            }
                          `}
                        >
                          <item.icon
                            className={`h-6 w-6 shrink-0 ${
                              location.pathname === item.href ? 'text-sky-600' : 'text-slate-400 group-hover:text-sky-600'
                            }`}
                            aria-hidden="true"
                          />
                          <span className="flex-1">{item.name}</span>
                          {item.feature && !hasFeature(item.feature) && (
                            <LockClosedIcon className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
              <li className="mt-auto">
                <div className="soft-separator border-t pt-4">
                  <div className="flex items-center gap-x-3 px-2 py-2">
                    <UserCircleIcon className="h-8 w-8 text-slate-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="group flex w-full gap-x-3 rounded-xl p-2 text-sm leading-6 font-semibold text-slate-600 transition-all duration-200 hover:bg-rose-50 hover:text-rose-600"
                  >
                    <ArrowLeftOnRectangleIcon className="h-6 w-6 shrink-0 text-slate-400 group-hover:text-rose-600" />
                    Logout
                  </button>
                  {instanceLabel && (
                    <div
                      className="mt-2 px-2 text-[11px] leading-tight text-slate-400"
                      title={instance.id ? `Instance ID: ${instance.id}` : undefined}
                    >
                      <span className="font-medium text-slate-500">{instanceLabel}</span>
                      {instance.environment && instance.environment !== 'production' && (
                        <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                          {instance.environment}
                        </span>
                      )}
                      {instance.version && <span className="ml-1">v{instance.version}</span>}
                      {showPlan && entitlements && (
                        <Link
                          to="/system-settings?tab=billing"
                          className="ml-1 text-slate-500 hover:text-sky-700 hover:underline"
                        >
                          · {entitlements.planName}
                        </Link>
                      )}
                      {showPlan && entitlements && entitlements.status !== 'active' && (
                        <span className="ml-1 rounded bg-rose-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-rose-700">
                          {entitlements.status === 'trialing' ? 'trial' : entitlements.status.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-x-3 px-2 text-[11px] text-slate-400">
                    <a
                      href={supportHelpUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-sky-600"
                    >
                      Help
                    </a>
                    {legalLinks.map((link) => (
                      <a
                        key={link.label}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-sky-600"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 m-2 flex h-16 shrink-0 items-center gap-x-4 rounded-2xl border border-slate-200/70 bg-white/85 px-4 shadow-sm backdrop-blur-md sm:gap-x-6 sm:px-6 lg:hidden">
          <button
            type="button"
            className="-m-2.5 rounded-xl p-2.5 text-slate-700 transition hover:bg-slate-100 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />
              <button
                onClick={handleLogout}
                className="focus-pop flex items-center gap-x-2 rounded-xl p-1.5 text-slate-700 transition hover:bg-rose-50 hover:text-rose-600"
              >
                <span className="sr-only">Logout</span>
                <ArrowLeftOnRectangleIcon className="h-6 w-6" />
                <span className="hidden lg:block text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>

        <main className="py-8">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
} 